import React, { useState } from 'react';
import { Globe, Users, Lock, Eye, EyeOff, Shield, AlertTriangle, Info, DollarSign, Star, LogIn } from 'lucide-react';

const PrivacySettings = ({
    value = 'public',
    password = '',
    passwordHint = '',
    isPaid = false,
    price = '',
    currency = 'USD',
    onChange,
    onPasswordChange,
    onHintChange,
    onIsPaidChange,
    onPriceChange,
    onCurrencyChange,
    disabled = false
}) => {
    const [showPassword, setShowPassword] = useState(false);

    const privacyOptions = [
        {
            id: 'public',
            label: 'Public',
            icon: Globe,
            description: 'Anyone can view this content',
            color: 'text-green-400',
            bgColor: 'bg-green-500/10 border-green-500/20'
        },
        {
            id: 'private',
            label: 'Private',
            icon: Users,
            description: 'Only you can view this content',
            color: 'text-blue-400',
            bgColor: 'bg-blue-500/10 border-blue-500/20'
        },
        {
            id: 'logged_in_required',
            label: 'Must Be Logged In',
            icon: LogIn,
            description: 'Any registered user can access after logging in',
            color: 'text-cyan-400',
            bgColor: 'bg-cyan-500/10 border-cyan-500/20'
        },
        {
            id: 'subscription_required',
            label: 'Supporters Only',
            icon: Star,
            description: 'Must have an active subscription to access',
            color: 'text-purple-400',
            bgColor: 'bg-purple-500/10 border-purple-500/20'
        },
        {
            id: 'password_protected',
            label: 'Password Protected',
            icon: Lock,
            description: 'Accessible with a password',
            color: 'text-yellow-400',
            bgColor: 'bg-yellow-500/10 border-yellow-500/20'
        },
        {
            id: 'secret',
            label: 'Secret',
            icon: Shield,
            description: 'Hidden from feeds, requires direct link + password',
            color: 'text-red-400',
            bgColor: 'bg-red-500/10 border-red-500/20'
        }
    ];

    const selectedOption = privacyOptions.find(opt => opt.id === value);
    const requiresPassword = value === 'password_protected' || value === 'secret';
    const paidActive = isPaid && value !== 'public';

    const handlePrivacyChange = (newValue) => {
        onChange(newValue);
        if (newValue !== 'password_protected' && newValue !== 'secret') {
            onPasswordChange && onPasswordChange('');
            onHintChange && onHintChange('');
        }
        if (newValue === 'public' && isPaid) {
            onIsPaidChange && onIsPaidChange(false);
            onPriceChange && onPriceChange('');
        }
    };

    const handlePaidToggle = () => {
        if (disabled || value === 'public') return;
        const next = !isPaid;
        onIsPaidChange && onIsPaidChange(next);
        if (!next) onPriceChange && onPriceChange('');
    };

    return (
        <div className="space-y-4">
            {/* Primary Access Level */}
            <div>
                <label className="block text-sm font-semibold text-gray-300 mb-3">
                    Content Privacy Level
                </label>
                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
                    {privacyOptions.map((option) => {
                        const Icon = option.icon;
                        const isSelected = value === option.id;
                        return (
                            <button
                                key={option.id}
                                type="button"
                                onClick={() => handlePrivacyChange(option.id)}
                                disabled={disabled}
                                className={`p-4 border-2 rounded-lg text-left transition-all duration-200 ${
                                    isSelected
                                        ? `${option.bgColor} ${option.color} border-current`
                                        : 'bg-gray-800/50 text-gray-400 border-gray-600 hover:border-gray-500 hover:bg-gray-700/50'
                                } ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                            >
                                <div className="flex items-start gap-3">
                                    <Icon className={`w-5 h-5 mt-0.5 ${isSelected ? option.color : 'text-gray-500'}`} />
                                    <div className="flex-1">
                                        <h4 className={`font-medium mb-1 ${isSelected ? option.color : 'text-gray-300'}`}>
                                            {option.label}
                                        </h4>
                                        <p className={`text-sm ${isSelected ? 'opacity-90' : 'text-gray-500'}`}>
                                            {option.description}
                                        </p>
                                    </div>
                                </div>
                            </button>
                        );
                    })}
                </div>
            </div>

            {/* Password settings */}
            {requiresPassword && (
                <div className="space-y-4 p-4 border border-yellow-500/30 rounded-lg bg-yellow-500/5">
                    <div className="flex items-center gap-2 text-yellow-400">
                        <AlertTriangle className="w-5 h-5" />
                        <h4 className="font-medium">Password Protection Settings</h4>
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-300 mb-2">Access Password *</label>
                        <div className="relative">
                            <input
                                type={showPassword ? 'text' : 'password'}
                                value={password}
                                onChange={(e) => onPasswordChange && onPasswordChange(e.target.value)}
                                className="w-full pr-10 py-2 px-3 bg-gray-800 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:border-yellow-500 focus:ring-1 focus:ring-yellow-500 transition-colors"
                                placeholder="Create a strong password"
                                disabled={disabled}
                                required
                                minLength="4"
                                maxLength="100"
                            />
                            <button
                                type="button"
                                onClick={() => setShowPassword(!showPassword)}
                                className="absolute inset-y-0 right-0 pr-3 flex items-center"
                            >
                                {showPassword
                                    ? <EyeOff className="w-4 h-4 text-gray-400 hover:text-gray-300" />
                                    : <Eye className="w-4 h-4 text-gray-400 hover:text-gray-300" />
                                }
                            </button>
                        </div>
                        <p className="text-xs text-gray-500 mt-1">This password will be required to access your content</p>
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-300 mb-2">Password Hint (Optional)</label>
                        <input
                            type="text"
                            value={passwordHint}
                            onChange={(e) => onHintChange && onHintChange(e.target.value)}
                            className="w-full py-2 px-3 bg-gray-800 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:border-yellow-500 focus:ring-1 focus:ring-yellow-500 transition-colors"
                            placeholder="Optional hint to help users remember the password"
                            disabled={disabled}
                            maxLength="500"
                        />
                        <p className="text-xs text-gray-500 mt-1">{passwordHint.length}/500 — shown to users before they enter the password</p>
                    </div>
                    {value === 'secret' && (
                        <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-3">
                            <div className="flex items-start gap-2">
                                <Info className="w-4 h-4 text-red-400 mt-0.5 flex-shrink-0" />
                                <div className="text-sm">
                                    <p className="text-red-400 font-medium mb-1">Secret Mode</p>
                                    <p className="text-red-300">
                                        This content will be completely hidden from public feeds and search.
                                        Only users with the direct link AND password can access it.
                                    </p>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            )}

            {/* Paid Version — separate toggle, stacks on top of the primary level */}
            <div className={`p-4 border rounded-lg transition-all ${
                value === 'public'
                    ? 'border-gray-700/50 bg-gray-800/20 opacity-60'
                    : paidActive
                        ? 'border-green-500/40 bg-green-500/5'
                        : 'border-gray-700 bg-gray-800/30'
            }`}>
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${paidActive ? 'bg-green-600/20' : 'bg-gray-700/50'}`}>
                            <DollarSign className={`w-5 h-5 ${paidActive ? 'text-green-400' : 'text-gray-500'}`} />
                        </div>
                        <div>
                            <h4 className={`font-semibold text-sm ${paidActive ? 'text-green-400' : 'text-gray-300'}`}>
                                Paid Version
                            </h4>
                            <p className="text-xs text-gray-500">
                                {value === 'public'
                                    ? 'Cannot be used with Public — select another privacy level first'
                                    : 'Require payment to access this content'}
                            </p>
                        </div>
                    </div>
                    <button
                        type="button"
                        onClick={handlePaidToggle}
                        disabled={disabled || value === 'public'}
                        aria-label="Toggle paid version"
                        className={`relative w-12 h-6 rounded-full transition-colors flex-shrink-0 ${
                            paidActive ? 'bg-green-600' : 'bg-gray-600'
                        } ${(disabled || value === 'public') ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                    >
                        <div className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${
                            paidActive ? 'translate-x-6' : 'translate-x-0'
                        }`} />
                    </button>
                </div>

                {paidActive && (
                    <div className="mt-4 space-y-3 pt-4 border-t border-green-500/20">
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            <div className="md:col-span-2">
                                <label className="block text-sm font-medium text-gray-300 mb-2">Price *</label>
                                <div className="relative">
                                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                        <DollarSign className="w-4 h-4 text-gray-400" />
                                    </div>
                                    <input
                                        type="number"
                                        value={price}
                                        onChange={(e) => onPriceChange && onPriceChange(e.target.value)}
                                        className="w-full pl-9 pr-3 py-2 bg-gray-800 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:border-green-500 focus:ring-1 focus:ring-green-500 transition-colors"
                                        placeholder="0.00"
                                        disabled={disabled}
                                        min="0.01"
                                        max="999.99"
                                        step="0.01"
                                    />
                                </div>
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-300 mb-2">Currency</label>
                                <select
                                    value={currency}
                                    onChange={(e) => onCurrencyChange && onCurrencyChange(e.target.value)}
                                    className="w-full py-2 px-3 bg-gray-800 border border-gray-600 rounded-lg text-white focus:border-green-500 focus:ring-1 focus:ring-green-500 transition-colors"
                                    disabled={disabled}
                                >
                                    <option value="USD">USD ($)</option>
                                    <option value="EUR">EUR (€)</option>
                                    <option value="GBP">GBP (£)</option>
                                    <option value="CAD">CAD ($)</option>
                                    <option value="AUD">AUD ($)</option>
                                </select>
                            </div>
                        </div>
                        <div className="bg-green-500/10 border border-green-500/20 rounded-lg p-3">
                            <div className="flex items-start gap-2">
                                <Info className="w-4 h-4 text-green-400 mt-0.5 flex-shrink-0" />
                                <p className="text-sm text-green-300">
                                    Users must purchase access to view this content.
                                    They must also meet the <strong className="text-green-400">{selectedOption?.label}</strong> requirement.
                                </p>
                            </div>
                        </div>
                    </div>
                )}
            </div>

            {/* Summary */}
            {selectedOption && (
                <div className={`p-3 rounded-lg border ${selectedOption.bgColor}`}>
                    <div className="flex items-center gap-2 flex-wrap">
                        <selectedOption.icon className={`w-4 h-4 ${selectedOption.color}`} />
                        <span className={`text-sm font-medium ${selectedOption.color}`}>{selectedOption.label}</span>
                        {paidActive && (
                            <>
                                <span className="text-gray-500 text-sm">+</span>
                                <DollarSign className="w-4 h-4 text-green-400" />
                                <span className="text-sm font-medium text-green-400">
                                    Paid{price ? ` (${currency} ${parseFloat(price).toFixed(2)})` : ''}
                                </span>
                            </>
                        )}
                    </div>
                    <p className="text-sm text-gray-400 mt-1">
                        {selectedOption.description}
                        {paidActive && price ? ` — ${currency} ${parseFloat(price).toFixed(2)} to purchase.` : ''}
                    </p>
                </div>
            )}
        </div>
    );
};

export default PrivacySettings;
